import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import type { SelectedItemRenderProps } from '@openx/components-core/lib/SelectionDrawer/types.ts';
import { type ComponentType, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import {
  SelectedContextualSegmentItem,
  type SelectedSegmentWithMode,
} from '../drawer/components/SelectedContextualSegmentItem.tsx';
import { type EnrichedSegment, getContextualSegmentKey } from '../utils/enrichment.ts';

export interface ContextualSegmentsListProps {
  includes: EnrichedSegment[];
  excludes: EnrichedSegment[];
  onRemove: (segment: EnrichedSegment) => void;
  readonly?: boolean;
  ItemComponent?: ComponentType<SelectedItemRenderProps<SelectedSegmentWithMode>>;
}

const MAIN_FORM_VISIBLE_SEGMENTS = 5;

type ContextualSegmentSummaryItemProps = {
  segment: EnrichedSegment;
  readonly: boolean;
  ItemComponent: ComponentType<SelectedItemRenderProps<SelectedSegmentWithMode>>;
  onRemove: (segment: EnrichedSegment) => void;
};

const ContextualSegmentSummaryItem = memo(function ContextualSegmentSummaryItem({
  segment,
  readonly,
  ItemComponent,
  onRemove,
}: ContextualSegmentSummaryItemProps) {
  const handleRemove = useCallback(() => onRemove(segment), [onRemove, segment]);
  const item: SelectedSegmentWithMode = {
    providerId: segment.providerId,
    segment: segment.metadata,
    inclusionType: segment.inclusionType,
    readonly,
    isMisconfigured: segment.isMisconfigured,
  };

  return <ItemComponent item={item} onRemove={handleRemove} />;
});

export const ContextualSegmentsList = memo(function ContextualSegmentsList({
  includes,
  excludes,
  onRemove,
  readonly = false,
  ItemComponent = SelectedContextualSegmentItem,
}: ContextualSegmentsListProps) {
  const { t } = useTranslation();
  const [showAllIncludes, setShowAllIncludes] = useState(false);
  const [showAllExcludes, setShowAllExcludes] = useState(false);

  const handleToggleIncludes = useCallback(() => setShowAllIncludes(previous => !previous), []);
  const handleToggleExcludes = useCallback(() => setShowAllExcludes(previous => !previous), []);
  const visibleIncludes = showAllIncludes ? includes : includes.slice(0, MAIN_FORM_VISIBLE_SEGMENTS);
  const visibleExcludes = showAllExcludes ? excludes : excludes.slice(0, MAIN_FORM_VISIBLE_SEGMENTS);

  return (
    <Box data-test="contextual-segments-list">
      {includes.length > 0 && (
        <Box data-test="included-segments-section" sx={{ mb: 2 }}>
          <FiltersTitle title={t('INCLUDED')} readonly marginTopValue={2} color="textSecondary" />
          <List disablePadding>
            {visibleIncludes.map(segment => (
              <ContextualSegmentSummaryItem
                key={getContextualSegmentKey({ providerId: segment.providerId, segmentId: segment.id })}
                segment={segment}
                readonly={readonly}
                ItemComponent={ItemComponent}
                onRemove={onRemove}
              />
            ))}
          </List>
          {includes.length > MAIN_FORM_VISIBLE_SEGMENTS && (
            <Button variant="text" color="primary" onClick={handleToggleIncludes}>
              {showAllIncludes ? t('Show less') : t('Show more')}
            </Button>
          )}
        </Box>
      )}

      {excludes.length > 0 && (
        <Box data-test="excluded-segments-section" sx={{ mb: 2 }}>
          <FiltersTitle title={t('EXCLUDED')} readonly marginTopValue={1} color="textSecondary" />
          <List disablePadding>
            {visibleExcludes.map(segment => (
              <ContextualSegmentSummaryItem
                key={getContextualSegmentKey({ providerId: segment.providerId, segmentId: segment.id })}
                segment={segment}
                readonly={readonly}
                ItemComponent={ItemComponent}
                onRemove={onRemove}
              />
            ))}
          </List>
          {excludes.length > MAIN_FORM_VISIBLE_SEGMENTS && (
            <Button variant="text" color="primary" onClick={handleToggleExcludes}>
              {showAllExcludes ? t('Show less') : t('Show more')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
});
